import type {
  DateType,
  IDate,
  ISingleDate,
  DatePrecision,
} from "./date";
import {
  MONTH_ABBREVIATIONS,
  MONTH_NAMES,
  createSingleDate,
  formatSingleDate,
  formatDate,
  formatDisplayDate,
  isDateUserDefined,
  serializeDate,
  validateDate,
  parseDuration,
  formatDuration,
  calculateToDateFromDuration,
} from "./date";

/**
 * Options for configuring the DateParser behavior.
 */
export interface IDateParserOptions {
  /**
   * Reference year to use when year is omitted from input (defaults to current calendar year).
   */
  referenceYear?: number;
  /**
   * Century pivot year for expanding 2-digit years.
   * Values <= pivot become 2000 + YY; values > pivot become 1900 + YY.
   * Default: 68 (00..68 -> 2000..2068, 69..99 -> 1969..1999).
   */
  centuryPivot?: number;
}

/**
 * Describes a date syntax permutation supported by DateParser.
 */
export interface IDatePermutation {
  id: string;
  category:
    | "Month & Year"
    | "Day & Month"
    | "Full Date & Time"
    | "Approximate Dates (~)"
    | "Date Ranges (to)"
    | "Date Ranges (- or to)"
    | "Duration Ranges"
    | "JSON Payload";
  syntax: string;
  example: string;
  description: string;
  expectedType: "date" | "range";
  expectedPrecision?: DatePrecision;
  expectedFormatted: string;
}

/**
 * Expands a 2-digit year into a 4-digit century-based year.
 *
 * @param {number} yy - Two-digit year (e.g. 26).
 * @param {number} [pivot=68] - Century pivot threshold.
 * @returns {number} 4-digit year (e.g. 2026).
 */
export function expandTwoDigitYear(yy: number, pivot = 68): number {
  if (yy >= 100) return yy;
  if (yy <= pivot) {
    return 2000 + yy;
  }
  return 1900 + yy;
}

/**
 * Resolves a month string (abbreviation, full name, or number) to a 1-12 month integer.
 *
 * @param {string} val - Month token (e.g. "Oct", "October", "10").
 * @returns {number} Month number 1..12 (defaults to 1 if unresolvable).
 */
export function resolveMonthNumber(val: string): number {
  const raw = val.trim().toLowerCase();
  const abbrevIdx = MONTH_ABBREVIATIONS.findIndex((m: string) => m.toLowerCase() === raw);
  if (abbrevIdx >= 0) return abbrevIdx + 1;
  const fullIdx = MONTH_NAMES.findIndex((m: string) => m.toLowerCase() === raw);
  if (fullIdx >= 0) return fullIdx + 1;
  // Handle variations like "sept"
  if (raw === "sept") return 9;
  const num = parseInt(val, 10);
  if (!isNaN(num) && num >= 1 && num <= 12) return num;
  return 1;
}

/**
 * Checks whether a string represents a valid month token (abbreviation or name).
 *
 * @param {string} token - Candidate month string.
 * @returns {boolean} True if token matches a known month.
 */
export function isMonthToken(token: string): boolean {
  const raw = token.trim().toLowerCase();
  return (
    MONTH_ABBREVIATIONS.some((m: string) => m.toLowerCase() === raw) ||
    MONTH_NAMES.some((m: string) => m.toLowerCase() === raw) ||
    raw === "sept"
  );
}

/**
 * Universal DateParser for parsing user text, partial dates, and serialized JSON definitions
 * into standard DatePicker-compatible IDate structures.
 */
export class DateParser {
  /**
   * Main entry point: Parses any date string, legacy payload, or JSON string into a standardized IDate.
   *
   * @param {string | IDate | null | undefined} input - Raw user text or JSON payload.
   * @param {IDateParserOptions} [options] - Optional parser configuration (e.g. referenceYear).
   * @returns {IDate} Normalized date payload matching DatePicker schema.
   */
  public static parse(
    input: string | IDate | null | undefined,
    options?: IDateParserOptions
  ): IDate {
    const fallbackNow = new Date();
    const defaultYear = options?.referenceYear ?? fallbackNow.getFullYear();
    const defaultMonth = fallbackNow.getMonth() + 1;
    const defaultDay = fallbackNow.getDate();
    const pivot = options?.centuryPivot ?? 68;

    const createDefault = (): IDate => {
      const from = createSingleDate("exact_date", defaultYear, defaultMonth, defaultDay);
      return {
        type: "date",
        from,
        formatted: formatSingleDate(from),
      };
    };

    if (!input) {
      return createDefault();
    }

    // 1. If already an object (IDate)
    if (typeof input === "object") {
      const payload = input as IDate;
      const isRange = String(payload.type || (payload as any).mode || "").toLowerCase() === "range" || Boolean(payload.duration);
      const type: DateType = isRange ? "range" : "date";
      const from = payload.from || (payload as any).single || createSingleDate("exact_date", defaultYear, defaultMonth, defaultDay);
      let to = payload.to;
      let duration = payload.duration;
      if (duration) {
        const parsedDur = parseDuration(duration);
        if (parsedDur) {
          duration = formatDuration(parsedDur);
          to = calculateToDateFromDuration(from, parsedDur);
        }
      }
      const normalized: IDate = {
        ...payload,
        type,
        from,
        to,
        duration,
        formatted: "",
      };
      normalized.formatted = formatDate(normalized);
      return normalized;
    }

    const trimmed = input.trim();
    if (!trimmed) {
      return createDefault();
    }

    // 2. If stored as JSON string
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      try {
        const parsed = JSON.parse(trimmed) as Record<string, any>;
        if (parsed && (parsed.type || parsed.mode || parsed.from || parsed.single || parsed.duration)) {
          const rawType = parsed.type || parsed.mode;
          const isRange = String(rawType || "").toLowerCase() === "range" || Boolean(parsed.duration);
          const type: DateType = isRange ? "range" : "date";
          const from = parsed.from || parsed.single || createSingleDate("exact_date", defaultYear, defaultMonth, defaultDay);
          let to = parsed.to;
          let duration = parsed.duration;
          if (duration) {
            const parsedDur = parseDuration(duration);
            if (parsedDur) {
              duration = formatDuration(parsedDur);
              to = calculateToDateFromDuration(from, parsedDur);
            }
          }
          const normalized: IDate = {
            ...parsed,
            type,
            from,
            to,
            duration,
            formatted: "",
          };
          normalized.formatted = formatDate(normalized);
          return normalized;
        }
      } catch {
        // Fall through to text parsing
      }
    }

    // 3. Date with duration expression (e.g. "10-Oct-2026 for 2 hours", "10-Oct-2026 + 3 days", "10-Oct-2026 (2 hrs)")
    const durationMatch = trimmed.match(
      /^(.*?)(?:[\s,]+(?:for|duration:?|\+|\(|-)?\s*|\s*\()\s*(\d+(?:\.\d+)?)\s*(minutes|minute|mins|min|m|hours|hour|hrs|hr|h|days|day|dys|dy|d|weeks|week|wks|wk|w|months|month|mos|mo|years|year|yrs|yr|y)\)?$/i
    );
    if (durationMatch) {
      const datePart = durationMatch[1].trim();
      const durRaw = `${durationMatch[2]} ${durationMatch[3]}`;
      const parsedDur = parseDuration(durRaw);
      if (parsedDur && datePart) {
        const fromPayload = DateParser.parseSingle(datePart, defaultYear, pivot);
        const canonicalDur = formatDuration(parsedDur);
        const toPayload = calculateToDateFromDuration(fromPayload, parsedDur);
        const rangePayload: IDate = {
          type: "range",
          from: fromPayload,
          to: toPayload,
          duration: canonicalDur,
          formatted: "",
        };
        rangePayload.formatted = formatDate(rangePayload);
        return rangePayload;
      }
    }

    // 4. Range Detection ("to" or "-" delimiter, case-insensitive)
    const rangeSplitRegex = /\s+(?:to|-|–|—)\s+/i;
    if (rangeSplitRegex.test(trimmed)) {
      const parts = trimmed.split(rangeSplitRegex);
      const fromPart = parts[0]?.trim();
      const toPart = parts.slice(1).join(" to ").trim();
      const fromPayload = DateParser.parseSingle(fromPart, defaultYear, pivot);
      const toPayload = DateParser.parseSingle(toPart, defaultYear, pivot);
      const rangePayload: IDate = {
        type: "range",
        from: fromPayload,
        to: toPayload,
        formatted: "",
      };
      rangePayload.formatted = formatDate(rangePayload);
      return rangePayload;
    }

    // 5. Single Date Parsing
    const single = DateParser.parseSingle(trimmed, defaultYear, pivot);
    return {
      type: "date",
      from: single,
      formatted: formatSingleDate(single),
    };
  }

  /**
   * Parses a single date token into an ISingleDate item.
   *
   * @param {string} raw - Input string (e.g. "Oct", "Oct-26", "~Oct-26", "09-Oct", "2026-10-14").
   * @param {number} defaultYear - Fallback reference year (e.g. 2026).
   * @param {number} pivot - Century pivot for 2-digit years.
   * @returns {ISingleDate} Single date representation.
   */
  public static parseSingle(raw: string, defaultYear: number, pivot = 68): ISingleDate {
    const trimmed = raw.trim();

    // Approximate prefix detection (~, about, c., c )
    const isApproximate =
      trimmed.startsWith("~") ||
      trimmed.toLowerCase().startsWith("about ") ||
      trimmed.toLowerCase().startsWith("c. ") ||
      trimmed.toLowerCase().startsWith("c ");

    const cleanStr = trimmed
      .replace(/^~\s*/, "")
      .replace(/^about\s+/i, "")
      .replace(/^c\.\s*/i, "")
      .replace(/^c\s+/i, "")
      .trim();

    // 1. Day-Month-Year with Time: "09-Oct-2026 16:20" or "09-Oct-26 16:20"
    const ddMmmYyyyTimeMatch = cleanStr.match(
      /^(\d{1,2})(?:st|nd|rd|th)?[-/\s]+([A-Za-z]{3,})[-/\s]+(\d{2,4})\s+(\d{1,2}:\d{2}(?::\d{2})?)$/
    );
    if (ddMmmYyyyTimeMatch && isMonthToken(ddMmmYyyyTimeMatch[2])) {
      const rawY = parseInt(ddMmmYyyyTimeMatch[3], 10);
      const y = rawY < 100 ? expandTwoDigitYear(rawY, pivot) : rawY;
      return createSingleDate(
        isApproximate ? "approximate_datetime" : "exact_datetime",
        y,
        resolveMonthNumber(ddMmmYyyyTimeMatch[2]),
        parseInt(ddMmmYyyyTimeMatch[1], 10),
        ddMmmYyyyTimeMatch[4],
        isApproximate
      );
    }

    // 2. ISO Date-Time: "2026-10-14 16:20"
    const isoDateTimeMatch = cleanStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})\s+(\d{1,2}:\d{2}(?::\d{2})?)$/);
    if (isoDateTimeMatch) {
      return createSingleDate(
        isApproximate ? "approximate_datetime" : "exact_datetime",
        parseInt(isoDateTimeMatch[1], 10),
        parseInt(isoDateTimeMatch[2], 10),
        parseInt(isoDateTimeMatch[3], 10),
        isoDateTimeMatch[4],
        isApproximate
      );
    }

    // 3. Day-Month-Year: "09-Oct-2026", "09-Oct-26", "9 Oct 2026", "9th Oct 2026"
    const ddMmmYyyyMatch = cleanStr.match(
      /^(\d{1,2})(?:st|nd|rd|th)?[-/\s]+([A-Za-z]{3,})[-/\s]+(\d{2,4})$/
    );
    if (ddMmmYyyyMatch && isMonthToken(ddMmmYyyyMatch[2])) {
      const rawY = parseInt(ddMmmYyyyMatch[3], 10);
      const y = rawY < 100 ? expandTwoDigitYear(rawY, pivot) : rawY;
      return createSingleDate(
        isApproximate ? "approximate_date" : "exact_date",
        y,
        resolveMonthNumber(ddMmmYyyyMatch[2]),
        parseInt(ddMmmYyyyMatch[1], 10),
        undefined,
        isApproximate
      );
    }

    // 4. ISO Date: "2026-10-14"
    const isoDateMatch = cleanStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (isoDateMatch) {
      return createSingleDate(
        isApproximate ? "approximate_date" : "exact_date",
        parseInt(isoDateMatch[1], 10),
        parseInt(isoDateMatch[2], 10),
        parseInt(isoDateMatch[3], 10),
        undefined,
        isApproximate
      );
    }

    // 5. Day-Month without Year (fills defaultYear): "09-Oct", "9 Oct", "9th Oct"
    const ddMmmNoYearMatch = cleanStr.match(/^(\d{1,2})(?:st|nd|rd|th)?[-/\s]+([A-Za-z]{3,})$/);
    if (ddMmmNoYearMatch && isMonthToken(ddMmmNoYearMatch[2])) {
      return createSingleDate(
        isApproximate ? "approximate_date" : "exact_date",
        defaultYear,
        resolveMonthNumber(ddMmmNoYearMatch[2]),
        parseInt(ddMmmNoYearMatch[1], 10),
        undefined,
        isApproximate
      );
    }

    // 6. Month-Year with 2-digit or 4-digit year: "Oct-26", "Oct 26", "Oct-2026", "October 2026"
    const mmmYyyyMatch = cleanStr.match(/^([A-Za-z]{3,})[-/\s]+(\d{2,4})$/);
    if (mmmYyyyMatch && isMonthToken(mmmYyyyMatch[1])) {
      const rawY = parseInt(mmmYyyyMatch[2], 10);
      const y = rawY < 100 ? expandTwoDigitYear(rawY, pivot) : rawY;
      return createSingleDate(
        "year_month",
        y,
        resolveMonthNumber(mmmYyyyMatch[1]),
        undefined,
        undefined,
        isApproximate
      );
    }

    // 7. Year-Month: "2026-10" or "2026-Oct"
    const yyyyMmMatch = cleanStr.match(/^(\d{4})[-/\s]+([A-Za-z]{3,}|\d{1,2})$/);
    if (yyyyMmMatch) {
      const monthToken = yyyyMmMatch[2];
      const m = isMonthToken(monthToken)
        ? resolveMonthNumber(monthToken)
        : parseInt(monthToken, 10);
      return createSingleDate(
        "year_month",
        parseInt(yyyyMmMatch[1], 10),
        m >= 1 && m <= 12 ? m : 1,
        undefined,
        undefined,
        isApproximate
      );
    }

    // 8. Month Only (fills defaultYear): "Oct", "October", "OCT"
    const monthOnlyMatch = cleanStr.match(/^([A-Za-z]{3,})$/);
    if (monthOnlyMatch && isMonthToken(monthOnlyMatch[1])) {
      return createSingleDate(
        "year_month",
        defaultYear,
        resolveMonthNumber(monthOnlyMatch[1]),
        undefined,
        undefined,
        isApproximate
      );
    }

    // 9. Year Only: "2026"
    const yearOnlyMatch = cleanStr.match(/^(\d{4})$/);
    if (yearOnlyMatch) {
      return createSingleDate(
        "year",
        parseInt(yearOnlyMatch[1], 10),
        undefined,
        undefined,
        undefined,
        isApproximate
      );
    }

    // 10. Fallback: JavaScript Date.parse
    const parsedTs = Date.parse(cleanStr);
    if (!isNaN(parsedTs)) {
      const d = new Date(parsedTs);
      return createSingleDate(
        isApproximate ? "approximate_date" : "exact_date",
        d.getFullYear(),
        d.getMonth() + 1,
        d.getDate(),
        undefined,
        isApproximate
      );
    }

    // 11. Last resort fallback to default date
    return createSingleDate("exact_date", defaultYear, 1, 1, undefined, isApproximate);
  }

  /**
   * Serializes a date payload to a JSON string matching DatePicker expectations.
   *
   * @param {IDate} payload - Date payload.
   * @returns {string} Stringified JSON representation.
   */
  public static serialize(payload: IDate): string {
    return serializeDate(payload);
  }

  /**
   * Formats a date payload into a human-readable display string.
   *
   * @param {IDate} payload - Date payload.
   * @returns {string} Coherent formatted string.
   */
  public static format(payload: IDate): string {
    return formatDate(payload);
  }

  /**
   * If the date is a JSON block (serialized string or object), returns the formatted text, not the JSON.
   *
   * @param {string | IDate | null | undefined} input - Raw date value.
   * @returns {string} Human-readable date string.
   */
  public static getDisplayDate(input: string | IDate | null | undefined): string {
    return formatDisplayDate(input);
  }

  /**
   * Checks whether a date payload or serialized JSON date block is userDefined.
   *
   * @param {string | IDate | null | undefined} input - Date payload or JSON string.
   * @returns {boolean} True if userDefined is true.
   */
  public static isUserDefined(input: string | IDate | null | undefined): boolean {
    return isDateUserDefined(input);
  }

  /**
   * Validates a date payload.
   *
   * @param {IDate} payload - Date payload.
   * @returns {boolean} True if valid according to date rules.
   */
  public static isValid(payload: IDate): boolean {
    return validateDate(payload).isValid;
  }

  /**
   * Returns a comprehensive list of all supported date syntax permutations
   * for interactive inspection in the Design Tester.
   *
   * @returns {IDatePermutation[]} Supported permutation specifications.
   */
  public static getSupportedPermutations(): IDatePermutation[] {
    return [
      {
        id: "perm-month-only",
        category: "Month & Year",
        syntax: "MMM / MMMM",
        example: "Oct",
        description: "Month name or abbreviation. Fills the missing year with the current year (2026).",
        expectedType: "date",
        expectedPrecision: "year_month",
        expectedFormatted: "Oct 2026",
      },
      {
        id: "perm-month-two-digit-year",
        category: "Month & Year",
        syntax: "MMM-YY / MMM YY",
        example: "Oct-26",
        description: "Month with 2-digit year. Adds the century (20) to expand 26 to 2026.",
        expectedType: "date",
        expectedPrecision: "year_month",
        expectedFormatted: "Oct 2026",
      },
      {
        id: "perm-month-full-year",
        category: "Month & Year",
        syntax: "MMM-YYYY / MMM YYYY",
        example: "Oct 2026",
        description: "Month with explicit four-digit year.",
        expectedType: "date",
        expectedPrecision: "year_month",
        expectedFormatted: "Oct 2026",
      },
      {
        id: "perm-approx-month-year",
        category: "Approximate Dates (~)",
        syntax: "~MMM-YY",
        example: "~Oct-26",
        description: "Approximate month with '~' symbol and 2-digit year. Expands century and marks approximate.",
        expectedType: "date",
        expectedPrecision: "year_month",
        expectedFormatted: "~ Oct 2026",
      },
      {
        id: "perm-approx-month-only",
        category: "Approximate Dates (~)",
        syntax: "~MMM",
        example: "~Oct",
        description: "Approximate month only with '~'. Fills current year (2026) and marks approximate.",
        expectedType: "date",
        expectedPrecision: "year_month",
        expectedFormatted: "~ Oct 2026",
      },
      {
        id: "perm-day-month-no-year",
        category: "Day & Month",
        syntax: "DD-MMM / D MMM",
        example: "09-Oct",
        description: "Calendar day and month without year. Fills in current year (2026).",
        expectedType: "date",
        expectedPrecision: "exact_date",
        expectedFormatted: "09-Oct-2026",
      },
      {
        id: "perm-day-month-two-digit-year",
        category: "Day & Month",
        syntax: "DD-MMM-YY",
        example: "09-Oct-26",
        description: "Calendar day, month, and 2-digit year. Expands century to 2026.",
        expectedType: "date",
        expectedPrecision: "exact_date",
        expectedFormatted: "09-Oct-2026",
      },
      {
        id: "perm-approx-day-month",
        category: "Approximate Dates (~)",
        syntax: "~DD-MMM",
        example: "~09-Oct",
        description: "Approximate calendar day without year. Fills current year (2026) with '~' indicator.",
        expectedType: "date",
        expectedPrecision: "approximate_date",
        expectedFormatted: "~ 09-Oct-2026",
      },
      {
        id: "perm-exact-date",
        category: "Full Date & Time",
        syntax: "DD-MMM-YYYY",
        example: "09-Oct-2026",
        description: "Standard full date representation.",
        expectedType: "date",
        expectedPrecision: "exact_date",
        expectedFormatted: "09-Oct-2026",
      },
      {
        id: "perm-iso-date",
        category: "Full Date & Time",
        syntax: "YYYY-MM-DD",
        example: "2026-10-14",
        description: "Standard ISO-8601 calendar date.",
        expectedType: "date",
        expectedPrecision: "exact_date",
        expectedFormatted: "14-Oct-2026",
      },
      {
        id: "perm-date-time",
        category: "Full Date & Time",
        syntax: "DD-MMM-YYYY HH:mm",
        example: "09-Oct-2026 16:20",
        description: "Exact calendar date with 24-hour time to the minute.",
        expectedType: "date",
        expectedPrecision: "exact_datetime",
        expectedFormatted: "09-Oct-2026 16:20",
      },
      {
        id: "perm-year-only",
        category: "Month & Year",
        syntax: "YYYY",
        example: "2026",
        description: "Historical or era milestone year only.",
        expectedType: "date",
        expectedPrecision: "year",
        expectedFormatted: "2026",
      },
      {
        id: "perm-range-month",
        category: "Date Ranges (to)",
        syntax: "MMM to MMM",
        example: "Oct to Nov",
        description: "Month span range without years. Fills current year (2026) for both sides.",
        expectedType: "range",
        expectedPrecision: "year_month",
        expectedFormatted: "Oct 2026 to Nov 2026",
      },
      {
        id: "perm-range-month-two-digit",
        category: "Date Ranges (to)",
        syntax: "MMM-YY to MMM-YY",
        example: "Oct-26 to Nov-26",
        description: "Month span with 2-digit years. Expands century for both endpoints.",
        expectedType: "range",
        expectedPrecision: "year_month",
        expectedFormatted: "Oct 2026 to Nov 2026",
      },
      {
        id: "perm-range-dates",
        category: "Date Ranges (to)",
        syntax: "DD-MMM-YYYY to DD-MMM-YYYY",
        example: "09-Oct-2026 to 10-Oct-2026",
        description: "Multi-day date range with 'to' delimiter.",
        expectedType: "range",
        expectedPrecision: "exact_date",
        expectedFormatted: "09-Oct-2026 to 10-Oct-2026",
      },
      {
        id: "perm-range-approximate",
        category: "Approximate Dates (~)",
        syntax: "~MMM-YY to ~MMM-YY",
        example: "~Oct-26 to ~Nov-26",
        description: "Approximate range with '~' markers on boundaries.",
        expectedType: "range",
        expectedPrecision: "year_month",
        expectedFormatted: "~ Oct 2026 to ~ Nov 2026",
      },
      {
        id: "perm-range-hyphen",
        category: "Date Ranges (- or to)",
        syntax: "DD-MMM-YYYY - DD-MMM-YYYY",
        example: "09-Oct-2026 - 12-Oct-2026",
        description: "Date range using spaced hyphen '-' separator.",
        expectedType: "range",
        expectedPrecision: "exact_date",
        expectedFormatted: "09-Oct-2026 to 12-Oct-2026",
      },
      {
        id: "perm-duration-hours",
        category: "Duration Ranges",
        syntax: "DD-MMM-YYYY HH:mm for X hours / hrs / h",
        example: "09-Oct-2026 14:00 for 2 hours",
        description: "Date with duration in hours. Automatically calculates end datetime.",
        expectedType: "range",
        expectedPrecision: "exact_datetime",
        expectedFormatted: "09-Oct-2026 14:00 (2 hours)",
      },
      {
        id: "perm-duration-days",
        category: "Duration Ranges",
        syntax: "DD-MMM-YYYY + X days / dys / d",
        example: "10-Oct-2026 + 3 days",
        description: "Date with duration in days. Automatically calculates end date.",
        expectedType: "range",
        expectedPrecision: "exact_date",
        expectedFormatted: "10-Oct-2026 (3 days)",
      },
      {
        id: "perm-duration-weeks",
        category: "Duration Ranges",
        syntax: "DD-MMM-YYYY (X weeks / wks / w)",
        example: "10-Oct-2026 (1 week)",
        description: "Date with duration in weeks. Automatically calculates end date.",
        expectedType: "range",
        expectedPrecision: "exact_date",
        expectedFormatted: "10-Oct-2026 (1 week)",
      },
      {
        id: "perm-duration-months",
        category: "Duration Ranges",
        syntax: "MMM YYYY for X months / mo / mos",
        example: "Oct 2026 for 2 months",
        description: "Month with duration in months. Automatically calculates end month.",
        expectedType: "range",
        expectedPrecision: "year_month",
        expectedFormatted: "Oct 2026 (2 months)",
      },
      {
        id: "perm-duration-minutes",
        category: "Duration Ranges",
        syntax: "DD-MMM-YYYY HH:mm + X mins / m",
        example: "09-Oct-2026 14:00 + 30 mins",
        description: "Datetime with duration in minutes.",
        expectedType: "range",
        expectedPrecision: "exact_datetime",
        expectedFormatted: "09-Oct-2026 14:00 (30 mins)",
      },
      {
        id: "perm-json-stored",
        category: "JSON Payload",
        syntax: '{"type":"date",...}',
        example: '{"type":"date","from":{"precision":"year_month","year":2026,"month":10}}',
        description: "Pre-stored JSON representation. Bypasses text parsing and retains structured configuration.",
        expectedType: "date",
        expectedPrecision: "year_month",
        expectedFormatted: "Oct 2026",
      },
    ];
  }
}

/**
 * Safely parses any date string, legacy date string, or JSON payload into an IDate.
 * Convenience functional wrapper around DateParser.parse.
 *
 * @param {string | IDate | null | undefined} input - Raw value to parse.
 * @returns {IDate} Parsed date payload.
 */
export function parseDate(input: string | IDate | null | undefined): IDate {
  return DateParser.parse(input);
}

